import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from 'src/shared/components/ui/sheet';
import { Input } from 'src/shared/components/ui/input';
import { Button } from 'src/shared/components/ui/button';
import Alert from 'src/shared/components/ui/alert';
import { useCreateCustomerMutation } from 'src/app/admin/state/api/adminGraphSlice';
import { isDuplicateCustomerIdError } from 'src/app/admin/state/api/queries/customerAdminQuery';
import { useEnsureOrganisationMutation, useRestoreOrganisationMutation } from 'src/shared/state/api-slices/organisationEngineSlice';

type AddOrganisationSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onInformation: (message: string) => void;
};

type Problem = { severity: 'error' | 'warning'; message: string };

const NOT_ADDED_TO_ENGINE: Problem = {
    severity: 'error',
    message: 'The engine did not add the organisation. Nothing was created. Try again.',
};
const NOT_RESTORED_IN_ENGINE: Problem = {
    severity: 'error',
    message: 'The engine did not restore the organisation. Nothing was created. Try again.',
};
const DUPLICATE_CUSTOMER_ID: Problem = { severity: 'error', message: 'An organisation with that Customer ID already exists.' };
const CREATE_FAILED: Problem = { severity: 'error', message: 'Something went wrong creating the organisation. Please try again.' };
const ADDED_IN_ENGINE_ONLY: Problem = {
    severity: 'warning',
    message: 'Added in the engine, but not yet in Nature Performance. Submit again to finish.',
};
const ALREADY_ON_PLATFORM = 'This organisation already existed on the platform; it is now onboarded into the Engine.';

const AddOrganisationSheet = ({ open, onOpenChange, onInformation }: AddOrganisationSheetProps) => {
    const [customerId, setCustomerId] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [problem, setProblem] = useState<Problem | null>(null);
    const [isAskingToRestore, setIsAskingToRestore] = useState(false);
    const [isEngineAhead, setIsEngineAhead] = useState(false);

    const [ensureOrganisation, { isLoading: isEnsuring }] = useEnsureOrganisationMutation();
    const [restoreOrganisation, { isLoading: isRestoring }] = useRestoreOrganisationMutation();
    const [createCustomer, { isLoading: isCreating }] = useCreateCustomerMutation();
    const isLoading = isEnsuring || isRestoring || isCreating;

    const trimmedId = customerId.trim();
    const trimmedName = customerName.trim();
    const canSubmit = trimmedId !== '' && trimmedName !== '' && !isLoading && !isAskingToRestore;
    const areDetailsLocked = isAskingToRestore || isEngineAhead;

    const resetForm = () => {
        setCustomerId('');
        setCustomerName('');
        setProblem(null);
        setIsAskingToRestore(false);
        setIsEngineAhead(false);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            resetForm();
        }
        onOpenChange(nextOpen);
    };

    const close = () => {
        resetForm();
        onOpenChange(false);
    };

    // The engine goes first (ADR 006). If Nature Performance then fails, a second submit with the same
    // details finishes the job, because the engine answers 409 for an organisation that it has.
    const addToNaturePerformance = async ({ engineChanged }: { engineChanged: boolean }) => {
        try {
            await createCustomer({ customerId: trimmedId, customerName: trimmedName }).unwrap();
            close();
        } catch (error) {
            if (isDuplicateCustomerIdError(error)) {
                if (engineChanged) {
                    onInformation(ALREADY_ON_PLATFORM);
                    close();
                } else {
                    setProblem(DUPLICATE_CUSTOMER_ID);
                }
            } else if (engineChanged) {
                setIsEngineAhead(true);
                setProblem(ADDED_IN_ENGINE_ONLY);
            } else {
                setProblem(CREATE_FAILED);
            }
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (canSubmit) {
            setProblem(null);
            const engineOutcome = await ensureOrganisation({ business_central_id: trimmedId, name: trimmedName })
                .unwrap()
                .catch(() => null);
            if (engineOutcome === null) {
                setProblem(NOT_ADDED_TO_ENGINE);
            } else if (engineOutcome === 'archived') {
                setIsAskingToRestore(true);
            } else {
                await addToNaturePerformance({ engineChanged: engineOutcome === 'created' });
            }
        }
    };

    const handleRestore = async () => {
        setIsAskingToRestore(false);
        const restored = await restoreOrganisation(trimmedId)
            .unwrap()
            .then(
                () => true,
                () => false
            );
        if (restored) {
            await addToNaturePerformance({ engineChanged: true });
        } else {
            setProblem(NOT_RESTORED_IN_ENGINE);
        }
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>Add organisation</SheetTitle>
                    <SheetDescription>Create a new customer organisation in the engine and in Nature Performance.</SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className='flex flex-col gap-4 mt-6' data-testid='add-organisation-form'>
                    <div className='flex flex-col gap-1'>
                        <label htmlFor='org-customer-id' className='text-sm font-medium'>
                            Customer ID
                        </label>
                        <Input
                            id='org-customer-id'
                            value={customerId}
                            onChange={event => setCustomerId(event.target.value)}
                            placeholder='e.g. C00123'
                            disabled={areDetailsLocked}
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label htmlFor='org-customer-name' className='text-sm font-medium'>
                            Customer name
                        </label>
                        <Input
                            id='org-customer-name'
                            value={customerName}
                            onChange={event => setCustomerName(event.target.value)}
                            placeholder='Organisation name'
                            disabled={areDetailsLocked}
                        />
                    </div>

                    {problem?.severity === 'error' && (
                        <p role='alert' className='text-sm text-red-100'>
                            {problem.message}
                        </p>
                    )}
                    {problem?.severity === 'warning' && <Alert severity='warning'>{problem.message}</Alert>}

                    {isAskingToRestore && (
                        <Alert severity='warning' data-testid='add-organisation-restore-question'>
                            <p>This organisation is archived in the engine. Restore it, then add it to Nature Performance?</p>
                            <div className='flex gap-2 mt-2'>
                                <Button type='button' size='sm' onClick={handleRestore}>
                                    Yes, restore
                                </Button>
                                <Button type='button' size='sm' variant='outline' onClick={() => setIsAskingToRestore(false)}>
                                    No
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <SheetFooter className='mt-2'>
                        <Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type='submit' disabled={!canSubmit} className='flex-grow'>
                            Add organisation
                        </Button>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
};

export default AddOrganisationSheet;
